import { type ReactNode, useState } from 'react';
import type { Table } from '@tanstack/react-table';
import {
  FilterClearAllButton,
  FilterMenuActiveBar,
  FilterMenuButton,
  filterActiveBarHasContent,
  QuickFilterTags,
  ToolbarCompactProvider,
  type FilterField,
  type QuickFilterPreset,
} from '@/components/ui/data-table-filter-toolbar';
import { toolbarContainerClass } from '@/lib/toolbar-compact';
import { cn } from '@/lib/utils';

/**
 * The deadlines filter row shared by the table and calendar. The same padding
 * and control height keep both views starting in line. The calendar omits
 * date presets; the table passes them in.
 */
export function DeadlinesFilterToolbar({
  table,
  fields,
  quickFilterPresets,
  toolbarExtra,
  toolbarExtraActiveCount = 0,
  onClearAllFilters,
}: {
  table: Table<any>;
  fields: FilterField[];
  quickFilterPresets: QuickFilterPreset[];
  toolbarExtra?: ReactNode;
  toolbarExtraActiveCount?: number;
  onClearAllFilters?: () => void;
}) {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterMenuFieldId, setFilterMenuFieldId] = useState<string | null>(null);

  const showFilterActiveBar = filterActiveBarHasContent(
    table,
    fields,
    quickFilterPresets,
    toolbarExtraActiveCount,
  );

  return (
    <div className="flex w-full flex-col gap-3 pb-3">
      <ToolbarCompactProvider
        className={cn(
          'flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2',
          toolbarContainerClass,
        )}
      >
        <div className="flex flex-1 items-center gap-2">
          {fields.length > 0 && (
            <FilterMenuButton
              table={table}
              fields={fields}
              open={filterMenuOpen}
              onOpenChange={(next) => {
                setFilterMenuOpen(next);
                if (!next) setFilterMenuFieldId(null);
              }}
              drillFieldId={filterMenuFieldId}
              onDrillFieldIdChange={setFilterMenuFieldId}
            />
          )}
          {toolbarExtra}
          {quickFilterPresets.length > 0 && (
            <>
              <div className="mx-0.5 hidden h-4 w-px bg-border sm:block" aria-hidden />
              <div className="flex items-center gap-0.5">
                <QuickFilterTags table={table} presets={quickFilterPresets} />
              </div>
            </>
          )}
          {fields.length > 0 && (
            <FilterClearAllButton
              table={table}
              fields={fields}
              onClearAll={onClearAllFilters}
              extraActiveCount={toolbarExtraActiveCount}
            />
          )}
        </div>
      </ToolbarCompactProvider>
      {showFilterActiveBar && (
        <FilterMenuActiveBar
          table={table}
          fields={fields}
          quickFilters={quickFilterPresets}
        />
      )}
    </div>
  );
}
